import * as THREE from "three"
import { gsap } from "gsap"
import {
  cloudsFolder,
  debugObject,
  jacketFolder,
  loaders,
  scene,
} from "./Experience"

import cloudsVertexShader from "../../shaders/clouds/vertex.glsl"
import cloudsFragmentShader from "../../shaders/clouds/fragment.glsl"

export class GarmentJacket {
  constructor() {
    this.customUniforms = {
      uTime: { value: 0 },
      uNoiseFrequency: { value: 1.9 },
      uColorFade: { value: 0.7 },
      uNoiseVariation: { value: 150 },
      uColor1: { value: new THREE.Color(debugObject.jacketColor1) },
      uColor2: { value: new THREE.Color(debugObject.jacketColor2) },
      uColor3: { value: new THREE.Color(debugObject.jacketColor3) },
      uColor4: { value: new THREE.Color(debugObject.jacketColor4) },
    }

    this.setJacket()
    this.setTweaks()
  }

  setJacket() {
    this.jacketNormalMap = loaders.textureLoader.load(
      "/assets/models/jacketNormal.jpg"
    )
    this.jacketNormalMap.flipY = false

    this.jacketMaterial = new THREE.MeshStandardMaterial({
      color: "#705cff",
      roughness: 0.8,
      metalness: 0.4,
      normalMap: this.jacketNormalMap,
    })

    this.jacketMaterial.onBeforeCompile = (shader) => {
      shader.uniforms.uTime = this.customUniforms.uTime

      shader.uniforms.uColor1 = this.customUniforms.uColor1
      shader.uniforms.uColor2 = this.customUniforms.uColor2
      shader.uniforms.uColor3 = this.customUniforms.uColor3
      shader.uniforms.uColor4 = this.customUniforms.uColor4

      shader.uniforms.uDepthRange = { value: new THREE.Vector2(1, 130) }
      shader.uniforms.uNoiseFrequency = this.customUniforms.uNoiseFrequency
      shader.uniforms.uColorFade = this.customUniforms.uColorFade
      shader.uniforms.uNoiseVariation = this.customUniforms.uNoiseVariation

      shader.vertexShader = shader.vertexShader.replace(
        "#include <common>",
        `
                #include <common>
                varying vec3 vPosition;
            `
      )

      shader.vertexShader = shader.vertexShader.replace(
        "#include <begin_vertex>",
        `
                #include <begin_vertex> 
                vPosition = position;
            `
      )

      shader.fragmentShader = shader.fragmentShader.replace(
        "#include <common>",
        `
                #include <common>

                uniform vec3 uColor1;
                uniform vec3 uColor2;
                uniform vec3 uColor3;
                uniform vec3 uColor4;
                uniform vec2 uDepthRange;
                uniform float uNoiseFrequency;
                uniform float uColorFade;
                uniform float uNoiseVariation;

                //	Classic Perlin 3D Noise 
                //	by Stefan Gustavson
                //
                vec4 permute(vec4 x){return mod(((x*34.0)+1.0)*x, 289.0);}
                vec4 taylorInvSqrt(vec4 r){return 1.79284291400159 - 0.85373472095314 * r;}
                vec3 fade(vec3 t) {return t*t*t*(t*(t*6.0-15.0)+10.0);}

                float cnoise(vec3 P){
                  vec3 Pi0 = floor(P); // Integer part for indexing
                  vec3 Pi1 = Pi0 + vec3(1.0); // Integer part + 1
                  Pi0 = mod(Pi0, 289.0);
                  Pi1 = mod(Pi1, 289.0);
                  vec3 Pf0 = fract(P); // Fractional part for interpolation
                  vec3 Pf1 = Pf0 - vec3(1.0); // Fractional part - 1.0
                  vec4 ix = vec4(Pi0.x, Pi1.x, Pi0.x, Pi1.x);
                  vec4 iy = vec4(Pi0.yy, Pi1.yy);
                  vec4 iz0 = Pi0.zzzz;
                  vec4 iz1 = Pi1.zzzz;
                
                  vec4 ixy = permute(permute(ix) + iy);
                  vec4 ixy0 = permute(ixy + iz0);
                  vec4 ixy1 = permute(ixy + iz1);
                
                  vec4 gx0 = ixy0 / 7.0;
                  vec4 gy0 = fract(floor(gx0) / 7.0) - 0.5;
                  gx0 = fract(gx0);
                  vec4 gz0 = vec4(0.5) - abs(gx0) - abs(gy0);
                  vec4 sz0 = step(gz0, vec4(0.0));
                  gx0 -= sz0 * (step(0.0, gx0) - 0.5);
                  gy0 -= sz0 * (step(0.0, gy0) - 0.5);
                
                  vec4 gx1 = ixy1 / 7.0;
                  vec4 gy1 = fract(floor(gx1) / 7.0) - 0.5;
                  gx1 = fract(gx1);
                  vec4 gz1 = vec4(0.5) - abs(gx1) - abs(gy1);
                  vec4 sz1 = step(gz1, vec4(0.0));
                  gx1 -= sz1 * (step(0.0, gx1) - 0.5);
                  gy1 -= sz1 * (step(0.0, gy1) - 0.5);
                
                  vec3 g000 = vec3(gx0.x,gy0.x,gz0.x);
                  vec3 g100 = vec3(gx0.y,gy0.y,gz0.y);
                  vec3 g010 = vec3(gx0.z,gy0.z,gz0.z);
                  vec3 g110 = vec3(gx0.w,gy0.w,gz0.w);
                  vec3 g001 = vec3(gx1.x,gy1.x,gz1.x);
                  vec3 g101 = vec3(gx1.y,gy1.y,gz1.y);
                  vec3 g011 = vec3(gx1.z,gy1.z,gz1.z);
                  vec3 g111 = vec3(gx1.w,gy1.w,gz1.w);
                
                  vec4 norm0 = taylorInvSqrt(vec4(dot(g000, g000), dot(g010, g010), dot(g100, g100), dot(g110, g110)));
                  g000 *= norm0.x;
                  g010 *= norm0.y;
                  g100 *= norm0.z;
                  g110 *= norm0.w;
                  vec4 norm1 = taylorInvSqrt(vec4(dot(g001, g001), dot(g011, g011), dot(g101, g101), dot(g111, g111)));
                  g001 *= norm1.x;
                  g011 *= norm1.y;
                  g101 *= norm1.z;
                  g111 *= norm1.w;
                
                  float n000 = dot(g000, Pf0);
                  float n100 = dot(g100, vec3(Pf1.x, Pf0.yz));
                  float n010 = dot(g010, vec3(Pf0.x, Pf1.y, Pf0.z));
                  float n110 = dot(g110, vec3(Pf1.xy, Pf0.z));
                  float n001 = dot(g001, vec3(Pf0.xy, Pf1.z));
                  float n101 = dot(g101, vec3(Pf1.x, Pf0.y, Pf1.z));
                  float n011 = dot(g011, vec3(Pf0.x, Pf1.yz));
                  float n111 = dot(g111, Pf1);
                
                  vec3 fade_xyz = fade(Pf0);
                  vec4 n_z = mix(vec4(n000, n100, n010, n110), vec4(n001, n101, n011, n111), fade_xyz.z);
                  vec2 n_yz = mix(n_z.xy, n_z.zw, fade_xyz.y);
                  float n_xyz = mix(n_yz.x, n_yz.y, fade_xyz.x); 
                  return 2.2 * n_xyz;
                }

                float linearizeDepth(float depth, float near, float far)
                {
                  float z = depth * 2.0 - 1.0;
                  return (2.0 * near * far / (far + near - z * (far - near)) - near) / (far-near);
                }

                varying vec3 vPosition;
            `
      )

      shader.fragmentShader = shader.fragmentShader.replace(
        "#include <color_fragment>",
        `
                #include <color_fragment> 

                float lineardepth = linearizeDepth(gl_FragCoord.z, uDepthRange[0], uDepthRange[1]);

                float noise = step(0.0, cnoise(vec3(vNormal) * uNoiseFrequency));

                // float strength1 = smoothstep(0.5, 0.51, lineardepth);
                // float strength2 = smoothstep(0.52, 0.53, lineardepth);
                // float strength3 = smoothstep(0.54, 0.55, lineardepth);

                float strength1 = smoothstep(0.0, uColorFade, cnoise(vec3(vNormal) * uNoiseFrequency));
                float strength2 = smoothstep(0.0, uColorFade, cnoise(vec3(vNormal) * uNoiseFrequency + uNoiseVariation));
                float strength3 = smoothstep(0.0, uColorFade, cnoise(vec3(vNormal) * uNoiseFrequency + uNoiseVariation * 2.0));

                vec3 color1 = mix(uColor1, uColor2, strength1);
                vec3 color2 = mix(color1, uColor3, strength2);
                vec3 color3 = mix(color2, uColor4, strength3);

                diffuseColor.rgb = vec3(color3);
                // diffuseColor.rgb = vec3(strength1);
                // diffuseColor.rgb = vec3(noise);
            `
      )
    }
    this.jacketMaterial.needsUpdate = true

    this.cloudsMaterial = new THREE.ShaderMaterial({
      vertexShader: cloudsVertexShader,
      fragmentShader: cloudsFragmentShader,
      transparent: true,

      uniforms: {
        uTime: { value: 0 },
        uAmplitude: { value: 10 },
        uFrequency: { value: 2 },
        uCloudsColor: { value: new THREE.Color(debugObject.cloudsColor) },
      },
    })

    loaders.gltfLoader.load("/assets/models/Jacket3.glb", (gltf) => {
      this.jacketGroup = gltf.scene

      this.jacketGroup.position.set(0, -1.1, 0.4)
      this.jacketGroup.rotation.set(0, -2.09, 0)

      // this.jacketGroup.position.set(0.3, 0, 1.35)
      // this.jacketGroup.rotation.set(0.5, -2.28, 0)

      this.jacket = this.jacketGroup.getObjectByName("Jacket")
      this.jacketMetal = this.jacketGroup.getObjectByName("JacketMetal")
      this.clouds = this.jacketGroup.getObjectByName("Clouds")

      this.jacket.material = this.jacketMaterial
      this.clouds.material = this.cloudsMaterial

      //Mask Mountain and all its elements
      this.jacketGroup.traverse((child) => {
        if (child.isMesh) {
          if (
            child.name == "Hologram" ||
            child.name === "InfoGraphic" ||
            child.name === "Mountain" ||
            child.name === "MountainBase" ||
            child.name === "TrailA" ||
            child.name === "TrailB" ||
            child.name === "TrailC" ||
            child.name === "Clouds"
          ) {
            child.material.stencilWrite = true
            child.material.stencilRef = 1
            child.material.stencilFunc = THREE.EqualStencilFunc
            child.renderOrder = 4
          }
        }
      })

      //Temp tweaks
      // jacketFolder.addInput(this.jacketGroup.position, "x", {
      //   min: -1,
      //   max: 1,
      //   step: 0.01,
      //   label: "PosX",
      // })
      // jacketFolder.addInput(this.jacketGroup.position, "y", {
      //   min: -1,
      //   max: 1,
      //   step: 0.01,
      //   label: "PosY",
      // })
      // jacketFolder.addInput(this.jacketGroup.position, "z", {
      //   min: -1,
      //   max: 3,
      //   step: 0.01,
      //   label: "PosZ",
      // })

      // jacketFolder.addInput(this.jacketGroup.rotation, "x", {
      //   min: -3,
      //   max: 3,
      //   step: 0.01,
      //   label: "RotX",
      // })
      // jacketFolder.addInput(this.jacketGroup.rotation, "y", {
      //   min: -3,
      //   max: 3,
      //   step: 0.01,
      //   label: "RotY",
      // })
      // jacketFolder.addInput(this.jacketGroup.rotation, "z", {
      //   min: -3,
      //   max: 3,
      //   step: 0.01,
      //   label: "RotZ",
      // })

      scene.add(this.jacketGroup)
    })
  }

  setTweaks() {
    jacketFolder.addInput(this.jacketMaterial, "roughness", {
      min: 0,
      max: 1,
      step: 0.01,
      label: "Jacket Roughness",
    })

    jacketFolder.addInput(this.jacketMaterial, "metalness", {
      min: 0,
      max: 1,
      step: 0.01,
      label: "Jacket Metallic",
    })

    jacketFolder.addInput(this.customUniforms.uNoiseFrequency, "value", {
      min: 0,
      max: 4,
      step: 0.01,
      label: "Noise Frequency",
    })

    jacketFolder.addInput(this.customUniforms.uColorFade, "value", {
      min: 0,
      max: 1,
      step: 0.01,
      label: "Color Fade",
    })

    jacketFolder.addInput(this.customUniforms.uNoiseVariation, "value", {
      min: 0,
      max: 2000,
      step: 1,
      label: "Noise Variation",
    })

    jacketFolder.addInput(debugObject, "jacketColor1").on("change", (ev) => {
      this.customUniforms.uColor1.value = new THREE.Color(ev.value)
    })

    jacketFolder.addInput(debugObject, "jacketColor2").on("change", (ev) => {
      this.customUniforms.uColor2.value = new THREE.Color(ev.value)
    })

    jacketFolder.addInput(debugObject, "jacketColor3").on("change", (ev) => {
      this.customUniforms.uColor3.value = new THREE.Color(ev.value)
    })

    jacketFolder.addInput(debugObject, "jacketColor4").on("change", (ev) => {
      this.customUniforms.uColor4.value = new THREE.Color(ev.value)
    })

    cloudsFolder.addInput(debugObject, "cloudsColor").on("change", (ev) => {
      this.clouds.material.uniforms.uCloudsColor.value = new THREE.Color(
        ev.value
      )
    })

    cloudsFolder.addInput(this.cloudsMaterial.uniforms.uAmplitude, "value", {
      min: 0,
      max: 20,
      step: 0.1,
      label: "Amplitude",
    })

    cloudsFolder.addInput(this.cloudsMaterial.uniforms.uFrequency, "value", {
      min: 0,
      max: 10,
      step: 0.1,
      label: "Frequency",
    })
  }

  update(elapsedTime) {
    this.customUniforms.uTime.value = elapsedTime

    this.cloudsMaterial.uniforms.uTime.value = elapsedTime
  }
}
